import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Divider,
  IconButton,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_CONFIG } from '../config/constants';
import { createCopilotProject, assignCampaignsToCandidates, startAutoSourcingMessaging, handleATSIcpUpdates } from '../utils/api';

/**
 * MessagingFormTab component that provides a form-based interface for outreach strategy setup
 * @returns {JSX.Element} MessagingFormTab component
 */
// Styled text field component matching footer styling
const StyledTextField = ({ multiline = false, rows, placeholder, value, onChange, width = '100%', type = 'text', inputProps = {} }) => (
  <Box
    sx={{
      width: width,
      bgcolor: '#FFFFFF',
      borderRadius: '16px',
      border: '1px solid #DCDFEA',
      boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 4px 16px -4px rgba(16, 24, 40, 0.08)',
      padding: '12px',
    }}
  >
    <TextField
      fullWidth
      multiline={multiline}
      rows={multiline ? rows : undefined}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      variant="standard"
      InputProps={{
        disableUnderline: true,
      }}
      inputProps={{
        style: {
          fontSize: '16px',
          fontFamily: 'Roboto',
          color: '#101828',
        },
        ...inputProps
      }}
      sx={{
        '& .MuiInputBase-root': {
          padding: '0px',
        },
        '& .MuiInputBase-input': {
          fontSize: '16px',
          fontFamily: 'Roboto',
          color: '#101828',
          backgroundColor: 'transparent',
        },
        '& .MuiInputBase-input::placeholder': {
          color: '#969DAD !important',
          opacity: '1 !important',
        },
        '& .MuiInputBase-input::-webkit-input-placeholder': { 
          color: '#969DAD !important',
          opacity: '1 !important',
        },
        '& .MuiInputBase-input::-moz-placeholder': { 
          color: '#969DAD !important',
          opacity: '1 !important',
        },
        '& input::placeholder': {
          color: '#969DAD !important',
          opacity: '1 !important',
        },
      }}
    />
  </Box>
);

const MessagingFormTab = ({
  userId,
  titleKeys,
  currentSessionId,
  formData = {
    interviewCount: 5,
    roleInfo: '',
    companyExcitement: '',
    timing: '',
    managerAppeal: '',
    prescreeningQuestions: [
      { question: '', acceptableAnswers: ['', '', ''] }
    ],
    scheduling: ''
  },
  setFormData,
  onSwitchToCandidates,
  matchResults
}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get role title from titleKeys or use default
  const roleTitle = titleKeys && titleKeys.length > 0 ? titleKeys[0] : 'Software Engineer';

  // Load saved form data when component mounts
  useEffect(() => {
    const loadFormData = async () => {
      if (currentSessionId && setFormData) {
        try {
          const response = await fetch(`${API_CONFIG.url}/api/chat-history/${userId}/${currentSessionId}`);
          if (response.ok) {
            const sessionData = await response.json();
            if (sessionData.messagingFormData) {
              setFormData(sessionData.messagingFormData);
            }
          }
        } catch (error) {
          console.error('Error loading form data:', error);
        }
      }
    };

    loadFormData();
  }, [currentSessionId, setFormData]);

  // Save form data periodically
  useEffect(() => {
    const saveFormData = async () => {
      if (currentSessionId && formData) {
        try {
          await fetch(`${API_CONFIG.url}/api/chat-history/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: userId,
              sessionId: currentSessionId,
              updates: {
                messagingFormData: formData
              }
            }),
          });
        } catch (error) {
          console.error('Error saving form data:', error);
        }
      }
    };

    // Save form data whenever it changes (with debounce)
    if (formData && Object.keys(formData).length > 0) {
      const timeoutId = setTimeout(saveFormData, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [formData, currentSessionId]);

  const handleInputChange = (field, value) => {
    if (setFormData) {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePrescreeningChange = (questionIndex, field, value, answerIndex = null) => {
    if (setFormData) {
      setFormData(prev => ({
        ...prev,
        prescreeningQuestions: prev.prescreeningQuestions.map((q, idx) => {
          if (idx === questionIndex) {
            if (field === 'question') {
              return { ...q, question: value };
            } else if (field === 'acceptableAnswers' && answerIndex !== null) {
              const newAnswers = [...q.acceptableAnswers];
              newAnswers[answerIndex] = value;
              return { ...q, acceptableAnswers: newAnswers };
            }
          }
          return q;
        })
      }));
    }
  };

  const addPrescreeningQuestion = () => {
    if (setFormData) {
      setFormData(prev => ({
        ...prev,
        prescreeningQuestions: [
          ...prev.prescreeningQuestions,
          { question: '', acceptableAnswers: ['', '', ''] }
        ]
      }));
    }
  };

  const removePrescreeningQuestion = (index) => {
    if (setFormData && formData.prescreeningQuestions.length > 1) {
      setFormData(prev => ({
        ...prev,
        prescreeningQuestions: prev.prescreeningQuestions.filter((_, idx) => idx !== index)
      }));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Here we make an API calls to start the sourcing and outreach generation
      console.log('Generating applicants with form data:', formData);

      // Pre-Created Campaign IDs for Auto-Sourcing
      // TODO: Populate with the correct Campaign IDs
      const primary_cid = '68a64a83455e7559ce10fec8';
      const job_hopper_cid = '68a64c5c455e75bdcf110006';
      const non_job_hopper_cid = '68a64cb8455e755241110047';
      const interview_cid = '68a64d4b455e757e371100a6';
      const exit_cid = '68a64d95455e75bb791100da';

      // Extract requirements from matchResults
      let requirements = [];
      if (matchResults && matchResults.search_gen_response && matchResults.search_gen_response.icp_requirements) {
        requirements = matchResults.search_gen_response.icp_requirements;
        console.log('Using requirements from matchResults:', requirements);
      } else {
        console.warn('No matchResults or icp_requirements found, using empty requirements array');
      }

      // Create project details based on role title and timestamp
      const project_name = `Copilot Search for Auto Sourcing - ${roleTitle}`;
      const description = `Auto sourcing campaign for ${roleTitle} position`;
      const text_prompt = `Created from intakemate for ${roleTitle} position`;

      console.log('Creating copilot project with:', {
        name: project_name,
        description,
        text_prompt,
        requirements_count: requirements.length,
        formData
      });

      // Call the API to create the copilot project with the requirements from matchResults
      const result = await createCopilotProject(
        project_name,
        description,
        text_prompt,
        requirements
      );

      console.log('Copilot project created successfully:', result);

      // Extract icp id and uid from the response
      const icpid = result.ref;
      const uid = result.uid;
      console.log('Extracted icp id field from copilot project:', icpid);

      if (icpid) {
        // Call assignCampaignsToCandidates with the campaign IDs
        const campaign_result = await assignCampaignsToCandidates(
          icpid, // icp_id from the ref field
          primary_cid,
          job_hopper_cid,
          non_job_hopper_cid,
          interview_cid,
          exit_cid,
          'user@example.com', // user_email
          uid, // user_id
          {} // campaign_variable_overrides
        );

        console.log('Campaigns assigned to candidates successfully:', campaign_result);

        // Fetch conversation messages from the current session
        let conversation_messages = [];
        if (currentSessionId) {
          try {
            const sessionResponse = await fetch(`${API_CONFIG.url}/api/chat-history/session/${currentSessionId}`);
            if (sessionResponse.ok) {
              const sessionData = await sessionResponse.json();
              if (sessionData.messages && Array.isArray(sessionData.messages)) {
                conversation_messages = sessionData.messages;
                console.log('Fetched conversation messages:', conversation_messages.length, 'messages');
              }
            }
          } catch (error) {
            console.error('Error fetching conversation messages:', error);
          }
        }

        // Prepare data for startAutoSourcingMessaging
        const conversation_blob = JSON.stringify(conversation_messages);

        const messaging_qa = formData.prescreeningQuestions
          .filter(q => q.question.trim() !== '')
          .map(q => ({
            question: q.question,
            answer: q.acceptableAnswers.filter(a => a.trim() !== '').join(', ')
          }));

        // Wait 5 seconds before calling startAutoSourcingMessaging
        console.log('Waiting 5 seconds before starting auto sourcing messaging...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        // Call startAutoSourcingMessaging with retries
        const retryStartAutoSourcingMessaging = async (maxRetries = 3) => {
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              console.log(`Starting auto sourcing messaging attempt ${attempt}/${maxRetries}...`);

              const auto_sourcing_result = await startAutoSourcingMessaging(
                uid, // joid (user ID)
                icpid, // icp_id
                primary_cid,
                job_hopper_cid,
                non_job_hopper_cid,
                interview_cid,
                exit_cid,
                formData.interviewCount, // interview_target
                conversation_blob,
                messaging_qa
              );

              console.log('Auto sourcing messaging started successfully:', auto_sourcing_result);
              return auto_sourcing_result;
            } catch (error) {
              console.error(`Auto sourcing messaging attempt ${attempt} failed:`, error);

              if (attempt === maxRetries) {
                throw error;
              }
              // Wait 5 seconds before the next attempt
              console.log(`Waiting 5 seconds before retry attempt ${attempt + 1}...`);
              await new Promise(resolve => setTimeout(resolve, 5000));
            }
          }
        };

        // Call startAutoSourcingMessaging with retry logic
        const auto_sourcing_result = await retryStartAutoSourcingMessaging();

        // Call handleATSIcpUpdates with default parameters
        const ats_result = await handleATSIcpUpdates(
          uid, // joid (user ID)
          icpid, // icp_id
          false, // export_all_campaign_cands_ats
          false, // export_as_prospect
          "replied", // export_emailids_criteria
          true, // disable_auto_ats_export
          [], // job_ids (empty array for now)
          ["notrightnow", "notinterested", "referral", "notamatch", "anyresponse", "interested"], // export_ats_suboptions
          { "stage_id": "Hired", "stage_name": "Hired" } // default_ats_export_stage
        );

        console.log('ATS ICP updates handled successfully:', ats_result);
      } else {
        console.warn('No icp id ref field found in copilot project response');
      }

      // Show success message or redirect
      alert('🎉 Perfect! Your outreach campaign is now being set up. We\'ll start identifying and reaching out to qualified candidates based on your requirements.');

    } catch (error) {
      console.error('Error generating applicants:', error);
      alert('There was an error setting up your outreach campaign. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F9FAFB',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E5E7EB',
            borderRadius: '4px',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1E293B' }}>
              Outreach Strategy Setup
            </Typography>
            {onSwitchToCandidates && (
              <Button
                variant="outlined"
                onClick={onSwitchToCandidates}
                sx={{
                  borderColor: '#DCDFEA',
                  color: '#374151',
                  fontWeight: 500,
                  padding: '8px 16px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '14px',
                  '&:hover': {
                    borderColor: '#9CA3AF',
                    backgroundColor: '#F9FAFB',
                  },
                }}
              >
                ← Back to Candidates
              </Button>
            )}
          </Box> */}
          <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.6 }}>
            Complete the form below to set up your outreach campaign for the <strong>{roleTitle}</strong> position. 
            All fields are optional, but providing more detail will help us create more compelling candidate messages.
          </Typography>
        </Box>

        {/* Interview Count */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Interview Target
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            How many people would you like to interview? We recommend starting with 5 candidates.
          </Typography>
          <StyledTextField
            type="number"
            value={formData.interviewCount}
            onChange={(e) => handleInputChange('interviewCount', parseInt(e.target.value) || 0)}
            width="150px"
            inputProps={{ min: 1, max: 50 }}
          />
        </Paper>

        {/* Role Information */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Role Appeal
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Why should a candidate be interested in this {roleTitle} role? What opportunities does it present for growth, learning, or impact?
          </Typography>
          <StyledTextField
            multiline
            rows={3}
            value={formData.roleInfo}
            onChange={(e) => handleInputChange('roleInfo', e.target.value)}
            placeholder="Describe the role's appeal, growth opportunities, learning potential, impact, etc."
          />
        </Paper>

        {/* Company Excitement */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Company Highlights
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            What can you share about your company that would get candidates excited? Include mission, values, problems you solve, market position, or anything relevant.
          </Typography>
          <StyledTextField
            multiline
            rows={3}
            value={formData.companyExcitement}
            onChange={(e) => handleInputChange('companyExcitement', e.target.value)}
            placeholder="Describe your company's mission, values, market impact, culture, etc."
          />
        </Paper>

        {/* Timing */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Why Now?
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Why is this a great time to join your company? Any recent wins, launches, funding, or milestones worth mentioning?
          </Typography>
          <StyledTextField
            multiline
            rows={3}
            value={formData.timing}
            onChange={(e) => handleInputChange('timing', e.target.value)}
            placeholder="Recent achievements, funding, product launches, growth milestones, etc."
          />
        </Paper>

        {/* Manager Appeal */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Working with You
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            What can you share about why a candidate should be excited to work with you and your team?
          </Typography>
          <StyledTextField
            multiline
            rows={3}
            value={formData.managerAppeal}
            onChange={(e) => handleInputChange('managerAppeal', e.target.value)}
            placeholder="Your management style, team culture, support for growth, collaboration approach, etc."
          />
        </Paper>

        {/* Prescreening Questions */}
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
              Prescreening Questions
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addPrescreeningQuestion}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Add Question
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            What questions would you like to ask candidates before scheduling interviews? Provide acceptable answer examples.
          </Typography>

          {formData.prescreeningQuestions.map((q, questionIndex) => (
            <Box key={questionIndex} sx={{ mb: 3, p: 2, bgcolor: '#FFFFFF', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Question {questionIndex + 1}
                </Typography>
                {formData.prescreeningQuestions.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removePrescreeningQuestion(questionIndex)}
                    sx={{ color: '#EF4444' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <StyledTextField
                  value={q.question}
                  onChange={(e) => handlePrescreeningChange(questionIndex, 'question', e.target.value)}
                  placeholder="Enter your prescreening question"
                />
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151', mb: 1 }}>
                Acceptable Answers (examples):
              </Typography>
              {q.acceptableAnswers.map((answer, answerIndex) => (
                <Box key={answerIndex} sx={{ mb: 1 }}>
                  <StyledTextField
                    value={answer}
                    onChange={(e) => handlePrescreeningChange(questionIndex, 'acceptableAnswers', e.target.value, answerIndex)}
                    placeholder={`Example acceptable answer ${answerIndex + 1}`}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Paper>

        {/* Scheduling */}
        <Paper sx={{ p: 3, mb: 4, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Scheduling
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            Do you have a scheduling link (Calendly, etc.) that candidates can use to book interviews?
          </Typography>
          <StyledTextField
            value={formData.scheduling}
            onChange={(e) => handleInputChange('scheduling', e.target.value)}
            placeholder="https://calendly.com/yourname/interview or describe your scheduling process"
          />
        </Paper>

        {/* Summary & Submit */}
        <Paper sx={{ p: 4, border: '2px solid #10B981', bgcolor: '#F0FDF4' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#047857', mb: 2 }}>
            Ready to Generate Applicants?
          </Typography>
          <Typography variant="body2" sx={{ color: '#065F46', mb: 3, lineHeight: 1.6 }}>
            We'll use the information above to create compelling, personalized outreach messages for qualified {roleTitle} candidates. 
            You can always come back and update these details later.
          </Typography>
          
          {/* Summary chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Chip label={`${formData.interviewCount} interviews`} size="small" color="success" />
            {formData.roleInfo && <Chip label="Role info provided" size="small" />}
            {formData.companyExcitement && <Chip label="Company highlights provided" size="small" />}
            {formData.timing && <Chip label="Timing info provided" size="small" />}
            {formData.managerAppeal && <Chip label="Manager appeal provided" size="small" />}
            {formData.prescreeningQuestions.some(q => q.question) && (
              <Chip label={`${formData.prescreeningQuestions.filter(q => q.question).length} prescreening questions`} size="small" />
            )}
            {formData.scheduling && <Chip label="Scheduling provided" size="small" />}
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              fontWeight: 600,
              padding: '12px 32px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              '&:hover': {
                backgroundColor: '#059669',
              },
              '&:disabled': {
                backgroundColor: '#9CA3AF',
              },
            }}
          >
            {isSubmitting ? 'Setting up campaign...' : 'Message & Generate Applicants'}
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default MessagingFormTab;
